<template>
    <div class="c-measurement-panel" v-if="appointment != null">
        <button class="btn-close" @click.prevent="closeDetail"></button>

        <div v-if="coconLoaded">
            <div v-if="allCoconsDisconnected && !isInShop" class="connection-error">
                {{$t('generic.cocon_connection_error')}}
            </div>
            <div v-else>
                <div v-if="isStatusKnown">
                    <div class="cocon-select" v-if="cocons > 1">
                        <button :id="btnId" class="value" @click.prevent="toggleCoconSelect">
                            <span>{{$t('cocon_status.cocon', {cocon : cocon + 1})}}</span>
                        </button>

                        <div class="options" v-if="showCoconSelect">
                            <div class="option" :class="{disabled : !isCoconConnected(c-1)}" v-for="c in cocons" :key="c" @click.prevent="setCurrentCocon(c-1)">
                                <span>{{$t('cocon_status.cocon', {cocon : c})}}</span>
                                <span class="cocon-status" :class="{disconnected : !isCoconConnected(c-1)}"></span>
                            </div>
                        </div>
                    </div>

                    <!-- type -->
                    <div v-if="!isMeasurementActive(appointment) && canStartMeasurement()">
                        <InputSelect v-model:value="type" :options="types" :editable="false" />
                    </div>

                    <div v-if="isMeasurementActive(appointment)" class="detail-info">
                        {{$t('measurements.'+getStatus(appointment).type)}}
                    </div>

                    <div v-if="!canStartMeasurement() && !isMeasurementActive(appointment)" class="detail-info">
                        {{$t('measurements.in_use')}}
                    </div>

                    <button :disabled="!canStartMeasurement() || !isValidAppointmentDate(appointment.date) || !clientDataComplete()" v-if="!isMeasurementActive(appointment)" class="btn-basic btn-action green" @click.prevent="startMeasurement">
                        {{$t('measurements.start')}}
                    </button>

                    <button v-if="isMeasurementActive(appointment)" class="btn-basic btn-action red" @click.prevent="stopMeasurement(appointment)">
                        {{$t('measurements.stop')}}
                    </button>

                    <div v-if="!clientDataComplete()" class="date-error">
                        {{$t('appointment_list.missing_user_data')}}
                    </div>

                    <div v-else-if="!isValidAppointmentDate(appointment.date)" class="date-error">
                        {{$t('measurements.no_date')}}
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            {{$t('generic.loading')}}...
        </div>

        <ConfirmPopup v-if="appointmentToStop != null"
            :title="$t('measurements.confirm_stop')"
            color="blue"
            @cancel="cancelStopPopup"
            @confirm="confirmStopMeasurement"
         />

        <ConfirmPopup v-if="bedErrorPopup"
            :title="$t('measurements.bed_error')"
            color="blue"
            :showCancel="false"
            @confirm="bedErrorPopup = false"
            yesLabel="ok"
         />
    </div>
</template>

<script>
import InputSelect from "@/components/InputSelect.vue";
import ConfirmPopup from "@/components/app/ConfirmPopup.vue";
import CoconMixin from "@/mixins/CoconMixin";
import DocumentClickMixin from "@/mixins/DocumentClickMixin";
import {apiStore} from "@/stores/api";
import {measurementStore} from "@/stores/measurement";
import {newDateFromString, formatTime, isValidDate} from "@/utils/utils";

export default {
    name : "MeasurementsPanel",

    components : {
        InputSelect,
        ConfirmPopup
    },

    mixins : [
        CoconMixin,
        DocumentClickMixin
    ],

    props: {
        appointment: {
            type: Object
        }
    },

    data() {
        return {
            appointmentToStop : null,
            type : null,
            types : [],
            cocon : 0,
            showCoconSelect : false,
            btnId : "cocon-select",
            coconLoaded : false,
            bedErrorPopup: false
        };
    },

    created() {
        this.fillTypes();
        this.type = this.types[0];
        if (this.isStaff) {
            this.type = this.types[1];
        }
        measurementStore().on("MOVIE_COMPLETED", this.onMovieCompleted);
        measurementStore().on("BED_NOT_READY", this.onBedNotReady);
    },

    unmounted() {
        measurementStore().off("MOVIE_COMPLETED", this.onMovieCompleted);
        measurementStore().off("BED_NOT_READY", this.onBedNotReady);
    },

    computed: {
        getType() {
            return this.types.findIndex(x => x === this.type) === 0 ? "long" : "short";
        },

        isStaff() {
            return this.appointment.clients.length > 0 && this.appointment.clients[0].additional_info.is_staff;
        }
    },

    watch: {
        status() {
            if (!this.coconLoaded) {
                this.coconLoaded = true;
            }
        },

        "appointment.id": {
            handler() {
                if (this.isStaff) {
                    this.type = this.types[1];
                }
            }
        },

        "$i18n.locale": {
            immediate: true,
            deep: true,
            handler() {
                const idx = this.types.findIndex(x => x === this.type);
                this.$nextTick(() => {
                    this.fillTypes();
                    if (idx > -1) {
                        this.type = this.types[idx];
                    }
                });

            }
        }
    },

    methods: {
        documentClickTriggered() {
            this.showCoconSelect = false;
        },

        closeDetail() {
            this.$emit("close");
        },

        canStartMeasurement() {
            if (this.cocon < this.status.length) {
                if (!this.isCoconConnected(this.cocon)) {
                    return false;
                }

                return !this.status[this.cocon].movie_active;
            } else {
                return false;
            }
        },

        startMeasurement() {
            let printLanguage = this.$i18n.locale;
            if (printLanguage.length > 2) {
                printLanguage = printLanguage.substring(2);
            }

            if (this.isCoconConnected(this.cocon)) {
                const data = {
                    appointment_id : this.appointment.id,
                    appointment: this.appointment,
                    auto_print: apiStore().shop ? apiStore().shop.auto_print : false,
                    print_language : printLanguage,
                    visitors : this.appointment.clients,
                    cocon : this.cocon,
                    short : this.getType === "short", // passed to cocon animation
                    service: false,
                    type: this.getType,
                    language : this.$i18n.locale
                };

                measurementStore().startMeasurement(data);
            }
        },

        stopMeasurement(appointment) {
            this.appointmentToStop = appointment;
        },

        cancelStopPopup() {
            this.appointmentToStop = null;
        },

        async confirmStopMeasurement() {
            const cocon = this.status.findIndex(x => x.appointment_id === this.appointmentToStop.id);
            measurementStore().stopMeasurement(cocon);

            this.updateAppointmentCompleted();

            this.appointmentToStop = null;
        },

        async updateAppointmentCompleted() {
            //set appointment to completed even when movie hasn't finished, yet
            if (this.appointment.clients[0].measurement_result !== null) {
                await apiStore().saveMovieCompleted(this.appointment.id);

                this.closeDetail();
            }
        },

        getStatus(appointment) {
            return this.status.find(x => x.appointment_id === appointment.id);
        },

        onMovieCompleted(data) {
            if (data.appointment_id === this.appointment.id) {
                this.closeDetail();
            }
        },

        onBedNotReady() {
            this.bedErrorPopup = true;
        },

        toggleCoconSelect() {
            this.showCoconSelect = !this.showCoconSelect;
        },

        setCurrentCocon(cocon) {
            if (this.isCoconConnected(cocon)) {
                this.cocon = cocon;
                this.showCoconSelect = false;
            }
        },

        fillTypes() {
            this.types = [
                this.$t("measurements.long"),
                this.$t("measurements.short")
            ];
        },

        isValidAppointmentDate(dateString) {
            const date = newDateFromString(dateString);
            return isValidDate(date) && formatTime(date) !== "00:00";
        },

        clientDataComplete() {
            if (this.appointment.clients.length === 0) {
                return false;
            }
            return this.appointment.clients.every(x => x.additional_info.gdpr !== undefined);
        }
    }
};
</script>

<style lang="scss" scoped>
    .c-measurement-panel {
        width : 100%;
        padding-top: 2rem;
        padding-left: 2%;
        position: relative;
        @include slideIntroAnimation(basicSlideIntro, 350ms, 50ms);

        .btn-close {
            @extend .reset-btn;
            background-image: url('/img/btn-close-popup.svg');
            background-repeat: no-repeat;
            width : 2rem;
            height : 2rem;
            position: absolute;
            top: -0.7rem;
            right: -0.7rem;
        }

        .detail-header {
            font-size: 1.2em;
            margin-bottom : 1.5rem;
        }

        .connection-error {
            color : $color-red;
            font-weight: bold;
        }

        :deep(.detail-pane) {
            padding-top: 3.5rem;
            @include slideIntroAnimation(basicSlideIntro, 350ms, 150ms);
        }

        .detail-info {
            font-size : 1.1em;
            margin-bottom: 2rem;
            text-align: center;
            margin-top: 1.6rem;
        }

        .date-error {
            font-size : 0.85em;
            color : $color-red;
            text-align: center;
            margin-top: 1.6rem;
        }

        .btn-action {
            height: 3.05rem;
            font-size: 1em;
            padding: 0 2.25rem;
            line-height: 2.75rem;
            padding: 0;
            width: 100%;
        }

        .cocon-select {
            width: 100%;
            margin: auto;
            position: relative;
            $borderColor : #efefef;

            button:disabled {
                opacity: .5;
            }

            .value {
                @extend .inputfieldMixin;
                width: 100%;
                color : $color-green;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 1.4em;

                img {
                    width : 2rem;
                    height : 2rem;
                    margin-left : 1rem;
                }

                * {
                    pointer-events: none;
                }
            }
            .options {
                position: absolute;
                width : 90%;
                left : 5%;
                z-index: 55;
                overflow-y:scroll;
                margin-top: -0.9rem;
                box-shadow: 0px 4px 6px 1px rgba(0,0,0,0.15);

            }

            .option {
                background : white;
                border-left : solid 1px $borderColor;
                border-right : solid 1px $borderColor;
                border-bottom : solid 1px $borderColor;
                padding : 1.5rem 1rem;
                font-size : 1.15em;
                display: flex;
                align-items: center;

                &:first-child {
                    border-top : solid 1px $borderColor;
                }

                &.disabled {
                    color: #BABABA;
                }

                .cocon-status {
                    background-image: url('/img/icon-checkbox.svg');
                    background-repeat: no-repeat;
                    width : 1em;
                    height : 1em;
                    margin-left : .5rem;

                    &.disconnected {
                        background-image: url('/img/icon-disconnected.svg');
                    }
                }
            }
        }
    }
</style>